import React, { useEffect, useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../../../components/ui/dialog";
import { Button } from "../../../components/ui/button";
import { api, isSessionExpired } from "../../../lib/api";
import { toast } from "sonner";
import { useImportReadiness } from "../hooks/useImportRowsData";
import { AlertTriangle, RefreshCw } from "lucide-react";

/**
 * Server-truth commit confirmation. Readiness is re-fetched immediately
 * before the final "Yes, commit" button is enabled. Blockers (invalid,
 * duplicates_unresolved, manual_review, unresolved_total) come from the
 * `/readiness` snapshot — never from the current rows page.
 *
 * Payload contract per backend `POST /commit`: no body; no `if_version`;
 * server computes commit atomically against its own stored job.version.
 *
 * On 400 `{code: "unresolved_rows", ...}` we re-read readiness (which
 * refreshes the UI blockers) and stay open.
 * On 409 illegal-state, we surface a conflict banner + refetch server truth.
 * On success we call onCommitted and close — the wizard shell then routes
 * to Step 5 via the fresh `job.status === "COMMITTING"`.
 */
export function CommitConfirmDialog({
  jobId,
  job,
  open,
  onClose,
  onCommitted,
}: {
  jobId: string;
  job: { counts?: Record<string, number> };
  open: boolean;
  onClose: () => void;
  onCommitted: () => void;
}) {
  const readyQ = useImportReadiness(jobId);
  const [inFlight, setInFlight] = useState(false);
  const [conflict, setConflict] = useState(false);
  const [refetching, setRefetching] = useState(false);

  useEffect(() => {
    if (open) {
      setConflict(false);
      readyQ.refetch(); // fresh readiness on open
    }
  }, [open]);

  const counts = job.counts ?? {};
  const total = Number(counts.total ?? 0);
  const valid = Number(counts.valid ?? 0);
  const invalid = Number(counts.invalid ?? readyQ.readiness.invalid ?? 0);
  const duplicates = Number(counts.duplicates_detected ?? 0);
  const unresolvedDupes = readyQ.readiness.duplicates_unresolved;
  const manualReview = readyQ.readiness.manual_review;
  const unresolvedTotal = readyQ.readiness.unresolved_total;
  const ready = readyQ.readiness.ready_for_commit;

  const doCommit = async () => {
    if (inFlight) return;
    // Fresh readiness re-check right before we submit
    setRefetching(true);
    await readyQ.refetch();
    setRefetching(false);
    if (!readyQ.readiness.ready_for_commit) return;

    setInFlight(true);
    setConflict(false);
    try {
      await api.post(`/import/jobs/${jobId}/commit`);
      toast.success("Import started");
      onCommitted();
      onClose();
    } catch (e: unknown) {
      if (isSessionExpired(e)) return;
      const status = (e as { response?: { status?: number } })?.response?.status;
      const detail = (e as { response?: { data?: { detail?: unknown } } })?.response
        ?.data?.detail;
      const code =
        typeof detail === "string"
          ? detail
          : typeof (detail as { code?: string })?.code === "string"
          ? (detail as { code: string }).code
          : "";
      if (code === "unresolved_rows") {
        // Re-read readiness so blocker cards update; keep dialog open.
        await readyQ.refetch();
        toast.error("Some rows still need resolution.");
      } else if (status === 409 || code.startsWith("illegal_state_for_commit")) {
        setConflict(true);
        onCommitted(); // force outer job refetch
      } else {
        toast.error("Could not start the import.");
      }
    } finally {
      setInFlight(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => (v ? null : onClose())}>
      <DialogContent
        className="max-w-lg max-h-[90vh] overflow-y-auto"
        data-testid="commit-confirm-dialog"
        aria-describedby="commit-confirm-desc"
      >
        <DialogHeader>
          <DialogTitle>Ready to import?</DialogTitle>
          <DialogDescription id="commit-confirm-desc">
            The durable worker will apply these rows. You can cancel while it runs.
          </DialogDescription>
        </DialogHeader>

        {conflict && (
          <div
            role="alert"
            className="flex items-start gap-2 rounded-md border border-amber-200 bg-amber-50 p-3 text-sm text-amber-900"
            data-testid="commit-conflict-banner"
          >
            <RefreshCw className="w-4 h-4 mt-0.5 shrink-0" />
            <div>Job state changed. Refresh and try again.</div>
          </div>
        )}

        <div
          className="grid grid-cols-2 gap-2 text-sm"
          data-testid="commit-summary"
          aria-live="polite"
        >
          <Line label="Total rows" value={total || valid + invalid + duplicates} />
          <Line label="Valid" value={valid} />
          <Line label="Invalid" value={invalid} tone={invalid ? "bad" : "muted"} />
          <Line label="Duplicates detected" value={duplicates} />
          <Line label="Unresolved duplicates" value={unresolvedDupes} tone={unresolvedDupes ? "warn" : "muted"} />
          <Line label="Manual review" value={manualReview} tone={manualReview ? "warn" : "muted"} />
        </div>

        {!ready && (
          <div
            role="alert"
            className="flex items-start gap-2 rounded-md border border-rose-200 bg-rose-50 p-3 text-sm text-rose-900"
            data-testid="commit-blocked-banner"
          >
            <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" />
            <div>
              {unresolvedTotal} row{unresolvedTotal === 1 ? "" : "s"} still need
              resolution before commit.
            </div>
          </div>
        )}

        <DialogFooter className="pt-2">
          <Button
            variant="ghost"
            className="rounded-full"
            onClick={onClose}
            disabled={inFlight}
            data-testid="commit-cancel-btn"
          >
            Cancel
          </Button>
          <Button
            className="rounded-full"
            disabled={!ready || inFlight || refetching || readyQ.loading}
            onClick={doCommit}
            data-testid="commit-confirm-btn"
          >
            {inFlight
              ? "Starting…"
              : refetching
              ? "Re-checking readiness…"
              : "Yes, start import"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function Line({
  label,
  value,
  tone,
}: {
  label: string;
  value: number;
  tone?: "muted" | "warn" | "bad";
}) {
  const cls =
    tone === "warn"
      ? "text-amber-800"
      : tone === "bad"
      ? "text-rose-800"
      : "text-slate-800";
  return (
    <div className="flex items-baseline justify-between border-b border-slate-100 py-1">
      <span className="text-slate-500 text-xs">{label}</span>
      <span className={`font-semibold ${cls}`}>{value}</span>
    </div>
  );
}
